import {
  FixedUtilitySeedColors,
  ForegroundVariant,
  ThemeColorAssignments,
  VariableSeedColors
} from "./types";

export const DISABLED_OPACITY = "0.3";
export const DISABLED_TEXT_OPACITY = "0.4";
export const HOVER_OVERLAY_OPACITY = 0.08;
export const ACTIVE_OVERLAY_OPACITY = 0.12;
export const BACKGROUND_OVERLAY_OPACITY = 0.04;
export const FOREGROUND_VARIANT_TO_OVERLAY_OPACITY_MAP: {
  [key in ForegroundVariant]: number;
} = {
  "1": 0.05,
  "2": 0.08,
  "3": 0.11
};

export const FREE_VARIABLE_SEED_COLORS: VariableSeedColors = {
  primary: "#0089FD",
  success: "#009968",
  warning: "#FFB025",
  error: "#EC0052",
  premium: "#8759EE"
};

export const PREMIUM_VARIABLE_SEED_COLORS: VariableSeedColors = {
  primary: "#8759EE",
  success: "#009968",
  warning: "#FFB025",
  error: "#EC0052",
  premium: "#8759EE"
};

export const PLURALL_VARIABLE_SEED_COLORS: VariableSeedColors = {
  primary: "#9649FF",
  success: "#009968",
  warning: "#FFB025",
  error: "#EC0052",
  premium: "#8759EE"
};

export const GOSTUDENT_LEARNING_VARIABLE_SEED_COLORS: VariableSeedColors = {
  primary: "#0B2FAC",
  success: "#4A9A6F",
  warning: "#E8A000",
  error: "#CC2F1F",
  premium: "#5F48D3"
};

export const NEUTRAL_SEED_COLOR_CHROMA = 16;

export const FIXED_NEUTRAL_MONOCHROME_SEED_COLOR = "#777777";

// Utility colour names approximated based on https://en.wikipedia.org/wiki/Color_term#/media/File:RBG_color_wheel.svg

export const FIXED_UTILITY_SEED_COLORS: FixedUtilitySeedColors = {
  magenta: "#CC71C4",
  violet: "#8565D1",
  blue: "#7E8AE0",
  azure: "#3599D1",
  cyan: "#00A1A4",
  green: "#3DA26E",
  chartreuse: "#889930",
  yellow: "#BD8713",
  orange: "#D8764A",
  pink: "#DE687E"
};

export const LIGHT_THEME_ASSIGNMENTS: ThemeColorAssignments = {
  "primary-high-surface": ["primary", "60"],
  "primary-medium-surface": ["primary", "90"],
  "primary-low-surface": ["primary", "95"],

  "success-high-surface": ["success", "60"],
  "success-medium-surface": ["success", "90"],
  "success-low-surface": ["success", "95"],

  "warning-high-surface": ["warning", "60"],
  "warning-medium-surface": ["warning", "90"],
  "warning-low-surface": ["warning", "95"],

  "error-high-surface": ["error", "60"],
  "error-medium-surface": ["error", "90"],
  "error-low-surface": ["error", "95"],

  "premium-high-surface": ["premium", "60"],
  "premium-medium-surface": ["premium", "90"],
  "premium-low-surface": ["premium", "95"],

  "neutral-high-surface": ["neutral", "60"],
  "neutral-medium-surface": ["neutralMono", "90"],
  "neutral-low-surface": ["neutralMono", "95"],

  "background-base": ["neutral", "100"],
  "background-overlay": ["primary", "50"],
  foreground: ["neutral", "100"],
  "foreground-variant-overlay": ["neutral", "50"],
  "hover-and-active-overlay": ["neutral", "40"],

  "primary-high-content": ["primary", "50"],
  "primary-medium-content": ["primary", "70"],
  "primary-low-content": ["primary", "80"],
  "primary-xLow-content": ["primary", "90"],
  "contentOn-primary-high-surface": ["primary", "100"],

  "success-high-content": ["success", "50"],
  "success-medium-content": ["success", "70"],
  "success-low-content": ["success", "80"],
  "success-xLow-content": ["success", "90"],
  "contentOn-success-high-surface": ["success", "100"],

  "warning-high-content": ["warning", "50"],
  "warning-medium-content": ["warning", "70"],
  "warning-low-content": ["warning", "80"],
  "warning-xLow-content": ["warning", "90"],
  "contentOn-warning-high-surface": ["warning", "100"],

  "error-high-content": ["error", "50"],
  "error-medium-content": ["error", "70"],
  "error-low-content": ["error", "80"],
  "error-xLow-content": ["error", "90"],
  "contentOn-error-high-surface": ["error", "100"],

  "premium-high-content": ["premium", "50"],
  "premium-medium-content": ["premium", "70"],
  "premium-low-content": ["premium", "80"],
  "premium-xLow-content": ["premium", "90"],
  "contentOn-premium-high-surface": ["premium", "100"],
  "neutral-high-content": ["neutral", "40"],
  "neutral-medium-content": ["neutral", "60"],
  "neutral-low-content": ["neutral", "80"],
  "neutral-xLow-content": ["neutralMono", "90"],
  "contentOn-neutral-high-surface": ["neutral", "100"],

  "magenta-utility": ["magenta", "60"],
  "violet-utility": ["violet", "60"],
  "blue-utility": ["blue", "60"],
  "azure-utility": ["azure", "60"],
  "cyan-utility": ["cyan", "60"],
  "green-utility": ["green", "60"],
  "chartreuse-utility": ["chartreuse", "60"],
  "yellow-utility": ["yellow", "60"],
  "orange-utility": ["orange", "60"],
  "pink-utility": ["pink", "60"],

  shadow: ["primary", "60"],
  "modal-overlay": ["neutral", "10"],
  white: ["neutral", "100"],
  logo: ["primary", "50"]
};

export const DARK_THEME_ASSIGNMENTS: ThemeColorAssignments = {
  "primary-high-surface": ["primary", "60"],
  "primary-medium-surface": ["primary", "30"],
  "primary-low-surface": ["primary", "10"],

  "success-high-surface": ["success", "60"],
  "success-medium-surface": ["success", "30"],
  "success-low-surface": ["success", "10"],

  "warning-high-surface": ["warning", "60"],
  "warning-medium-surface": ["warning", "30"],
  "warning-low-surface": ["warning", "10"],

  "error-high-surface": ["error", "60"],
  "error-medium-surface": ["error", "30"],
  "error-low-surface": ["error", "10"],

  "premium-high-surface": ["premium", "60"],
  "premium-medium-surface": ["premium", "30"],
  "premium-low-surface": ["premium", "10"],

  "neutral-high-surface": ["neutral", "60"],
  "neutral-medium-surface": ["neutral", "30"],
  "neutral-low-surface": ["neutral", "10"],

  "background-base": ["neutral", "14"],
  "background-overlay": ["neutral", "14"],
  foreground: ["neutral", "20"],
  "foreground-variant-overlay": ["neutral", "100"],
  "hover-and-active-overlay": ["neutral", "100"],

  "primary-high-content": ["primary", "70"],
  "primary-medium-content": ["primary", "50"],
  "primary-low-content": ["primary", "40"],
  "primary-xLow-content": ["primary", "30"],
  "contentOn-primary-high-surface": ["primary", "20"],

  "success-high-content": ["success", "70"],
  "success-medium-content": ["success", "50"],
  "success-low-content": ["success", "40"],
  "success-xLow-content": ["success", "30"],
  "contentOn-success-high-surface": ["success", "20"],

  "warning-high-content": ["warning", "70"],
  "warning-medium-content": ["warning", "50"],
  "warning-low-content": ["warning", "40"],
  "warning-xLow-content": ["warning", "30"],
  "contentOn-warning-high-surface": ["warning", "20"],

  "error-high-content": ["error", "70"],
  "error-medium-content": ["error", "50"],
  "error-low-content": ["error", "40"],
  "error-xLow-content": ["error", "30"],
  "contentOn-error-high-surface": ["error", "20"],

  "premium-high-content": ["premium", "70"],
  "premium-medium-content": ["premium", "50"],
  "premium-low-content": ["premium", "40"],
  "premium-xLow-content": ["premium", "30"],
  "contentOn-premium-high-surface": ["premium", "20"],

  "neutral-high-content": ["neutral", "80"],
  "neutral-medium-content": ["neutral", "60"],
  "neutral-low-content": ["neutral", "40"],
  "neutral-xLow-content": ["neutral", "30"],
  "contentOn-neutral-high-surface": ["neutral", "20"],

  "magenta-utility": ["magenta", "60"],
  "violet-utility": ["violet", "60"],
  "blue-utility": ["blue", "60"],
  "azure-utility": ["azure", "60"],
  "cyan-utility": ["cyan", "60"],
  "green-utility": ["green", "60"],
  "chartreuse-utility": ["chartreuse", "60"],
  "yellow-utility": ["yellow", "60"],
  "orange-utility": ["orange", "60"],
  "pink-utility": ["pink", "60"],

  shadow: ["primary", "0"],
  "modal-overlay": ["neutral", "10"],
  white: ["neutral", "100"],
  logo: ["primary", "70"]
};
